import type { LocationQuery, RouteLocationRaw } from 'vue-router'
import type { TypeUserPermission } from '../../types/User'
import { formatPercentNumber } from '../../utils/formatPercentNumber'
import type { InventoryHealthSummaryApi } from './types'

export type InventoryStockStatus = 'in_stock' | 'low_stock' | 'out_of_stock'
export type InventoryExpiryStatus = 'expiring_soon' | 'expired'

export type InventoryHealthCardId = 'low_stock' | 'out_of_stock' | 'expiring_soon' | 'expired'

export type InventoryHealthFilter = {
  stock_status?: InventoryStockStatus
  expiry_status?: InventoryExpiryStatus
}

export type InventoryHealthSummary = {
  inStock: number
  lowStock: number
  outOfStock: number
  expiringSoon: number
  expired: number
  expiredBatches: number
  inStockPercentage: number
  lowStockPercentage: number
  outOfStockPercentage: number
  expiringSoonPercentage: number
  expiredPercentage: number
  totalActiveItems: number
  totalActiveBatches: number
  totalLocations: number
  expiryAlertDays: number
  locationId: number | null
}

export type InventoryHealthCardGroup = 'stock' | 'expiry'

export type InventoryHealthCardColor = 'warning' | 'danger' | 'info' | 'purple'

export type InventoryHealthCardBadge = 'needsAttention' | 'planAction' | 'takeAction'

export type InventoryHealthCard = {
  id: InventoryHealthCardId
  group: InventoryHealthCardGroup
  label: string
  subtitle: string
  ctaLabel: string
  badge: InventoryHealthCardBadge
  icon: string
  color: InventoryHealthCardColor
  count: number
  filter: InventoryHealthFilter
}

export type InventoryGlanceMetricId = 'total_items' | 'total_batches' | 'total_locations' | 'items_in_stock'

export type InventoryGlanceMetric = {
  id: InventoryGlanceMetricId
  label: string
  value: number
  /** Only set when backed by API or session data — never static filler copy. */
  subtitle?: string
  icon: string
  color: 'success' | 'info' | 'purple' | 'teal'
}

export type InventoryBreakdownRowId = 'in_stock' | 'low_stock' | 'out_of_stock'

export type InventoryBreakdownRowColor = 'success' | 'warning' | 'danger'

export type InventoryIssueBreakdownRow = {
  id: InventoryBreakdownRowId
  label: string
  count: number
  percentage: number
  color: InventoryBreakdownRowColor
  chartColor: string
}

export type InventoryQuickActionId = 'adjust_stock' | 'view_reports' | 'manage_items' | 'stock_in'

export type InventoryQuickAction = {
  id: InventoryQuickActionId
  label: string
  icon: string
  color: 'info' | 'purple' | 'success' | 'secondary'
  to: RouteLocationRaw
  /** Permission gate: hide when the user cannot perform this action. */
  permission: { action: keyof TypeUserPermission; subject: string }
}

/** vue-i18n `t` (or a test stub) for health card / filter labels. */
export type InventoryHealthTranslate = (key: string, values?: Record<string, unknown>) => string

const STOCK_STATUSES: InventoryStockStatus[] = ['in_stock', 'low_stock', 'out_of_stock']
const EXPIRY_STATUSES: InventoryExpiryStatus[] = ['expiring_soon', 'expired']

/** Chart.js segment colors for the stock-status doughnut. */
export const INVENTORY_ISSUE_CHART_COLORS: Record<InventoryBreakdownRowColor, string> = {
  success: '#16A34A',
  warning: '#F59E0B',
  danger: '#EF4444',
}

const toCount = (value: unknown): number => {
  const n = Number(value)
  return Number.isInteger(n) && n > 0 ? n : 0
}

const toNonNegativeNumber = (value: unknown): number => {
  const n = Number(value)
  return Number.isFinite(n) && n >= 0 ? n : 0
}

export const emptyInventoryHealthSummary = (): InventoryHealthSummary => ({
  inStock: 0,
  lowStock: 0,
  outOfStock: 0,
  expiringSoon: 0,
  expired: 0,
  expiredBatches: 0,
  inStockPercentage: 0,
  lowStockPercentage: 0,
  outOfStockPercentage: 0,
  expiringSoonPercentage: 0,
  expiredPercentage: 0,
  totalActiveItems: 0,
  totalActiveBatches: 0,
  totalLocations: 0,
  expiryAlertDays: 0,
  locationId: null,
})

/** Map the API payload to dashboard counts. Missing or invalid values become 0. */
export function parseInventoryHealthSummary(raw: InventoryHealthSummaryApi | null | undefined): InventoryHealthSummary {
  if (!raw) return emptyInventoryHealthSummary()

  const locationId = raw.location_id == null ? null : Number(raw.location_id)

  return {
    inStock: toCount(raw.stock_status?.in_stock),
    lowStock: toCount(raw.stock_status?.low_stock),
    outOfStock: toCount(raw.stock_status?.out_of_stock),
    expiringSoon: toCount(raw.expiry_status?.expiring_soon),
    expired: toCount(raw.expiry_status?.expired),
    expiredBatches: toCount(raw.expired_batches),
    inStockPercentage: toNonNegativeNumber(raw.stock_status_percentages?.in_stock),
    lowStockPercentage: toNonNegativeNumber(raw.stock_status_percentages?.low_stock),
    outOfStockPercentage: toNonNegativeNumber(raw.stock_status_percentages?.out_of_stock),
    expiringSoonPercentage: toNonNegativeNumber(raw.expiry_status_percentages?.expiring_soon),
    expiredPercentage: toNonNegativeNumber(raw.expiry_status_percentages?.expired),
    totalActiveItems: toCount(raw.total_active_items),
    totalActiveBatches: toCount(raw.total_active_batches),
    totalLocations: toCount(raw.total_locations),
    expiryAlertDays: toCount(raw.expiry_alert_days),
    locationId: Number.isFinite(locationId) ? locationId : null,
  }
}

/** Sum of the four attention-card counts. */
export function inventoryAttentionCount(summary: InventoryHealthSummary): number {
  return summary.lowStock + summary.outOfStock + summary.expiringSoon + summary.expired
}

/** Four attention cards. Counts come from the summary API, never hardcoded. */
export function inventoryHealthCards(
  summary: InventoryHealthSummary,
  t: InventoryHealthTranslate,
  locationName?: string | null,
): InventoryHealthCard[] {
  const alertDays = summary.expiryAlertDays
  const expiringSubtitle =
    alertDays > 0
      ? t('inventoryHealth.expiringSoonSubtitleDays', { days: alertDays })
      : t('inventoryHealth.expiringSoonSubtitle')
  const expiredSubtitle =
    summary.expiredBatches > 0 && locationName
      ? t('inventoryHealth.expiredSubtitleBatches', {
          count: summary.expiredBatches,
          location: locationName,
        })
      : t('inventoryHealth.expiredSubtitle')

  return [
    {
      id: 'low_stock',
      group: 'stock',
      label: t('inventoryHealth.lowStock'),
      subtitle: t('inventoryHealth.lowStockSubtitle'),
      ctaLabel: t('inventoryHealth.viewLowStock'),
      badge: 'needsAttention',
      icon: 'inventory_2',
      color: 'warning',
      count: summary.lowStock,
      filter: { stock_status: 'low_stock' },
    },
    {
      id: 'out_of_stock',
      group: 'stock',
      label: t('inventoryHealth.outOfStock'),
      subtitle: t('inventoryHealth.outOfStockSubtitle'),
      ctaLabel: t('inventoryHealth.viewOutOfStock'),
      badge: 'needsAttention',
      icon: 'block',
      color: 'danger',
      count: summary.outOfStock,
      filter: { stock_status: 'out_of_stock' },
    },
    {
      id: 'expiring_soon',
      group: 'expiry',
      label: t('inventoryHealth.expiringSoon'),
      subtitle: expiringSubtitle,
      ctaLabel: t('inventoryHealth.viewExpiring'),
      badge: 'planAction',
      icon: 'calendar_month',
      color: 'info',
      count: summary.expiringSoon,
      filter: { expiry_status: 'expiring_soon' },
    },
    {
      id: 'expired',
      group: 'expiry',
      label: t('inventoryHealth.expired'),
      subtitle: expiredSubtitle,
      ctaLabel: t('inventoryHealth.viewExpired'),
      badge: 'takeAction',
      icon: 'event_busy',
      color: 'purple',
      count: summary.expired,
      filter: { expiry_status: 'expired' },
    },
  ]
}

/** Totals strip under the attention cards. */
export function inventoryGlanceMetrics(
  summary: InventoryHealthSummary,
  t: InventoryHealthTranslate,
  locationName?: string | null,
): InventoryGlanceMetric[] {
  const others = Math.max(summary.totalLocations - 1, 0)
  const locationsSubtitle =
    others > 0 && locationName
      ? t('inventoryHealth.totalLocationsSubtitleOthers', { location: locationName, count: others })
      : locationName
        ? t('inventoryHealth.totalLocationsSubtitleNamed', { location: locationName })
        : undefined

  const inStockSubtitle =
    summary.inStockPercentage > 0
      ? t('inventoryHealth.itemsInStockSubtitle', { percent: formatPercentNumber(summary.inStockPercentage) })
      : undefined

  return [
    {
      id: 'total_items',
      label: t('inventoryHealth.totalItems'),
      value: summary.totalActiveItems,
      icon: 'inventory_2',
      color: 'success',
    },
    {
      id: 'total_batches',
      label: t('inventoryHealth.totalBatches'),
      value: summary.totalActiveBatches,
      icon: 'layers',
      color: 'info',
    },
    {
      id: 'total_locations',
      label: t('inventoryHealth.totalLocations'),
      value: summary.totalLocations,
      subtitle: locationsSubtitle,
      icon: 'sell',
      color: 'purple',
    },
    {
      id: 'items_in_stock',
      label: t('inventoryHealth.itemsInStock'),
      value: summary.inStock,
      subtitle: inStockSubtitle,
      icon: 'check_box',
      color: 'teal',
    },
  ]
}

/**
 * Stock-status donut + legend. In / low / out of stock so % can complete to ~100%.
 * Counts drive the chart; legend % comes from the API (not recomputed client-side).
 * Expiry statuses stay on the attention cards only.
 */
export function inventoryIssueBreakdown(
  summary: InventoryHealthSummary,
  t: InventoryHealthTranslate,
): { total: number; rows: InventoryIssueBreakdownRow[] } {
  const rows: InventoryIssueBreakdownRow[] = [
    {
      id: 'in_stock',
      label: t('inventoryHealth.itemsInStock'),
      count: summary.inStock,
      percentage: summary.inStockPercentage,
      color: 'success',
      chartColor: INVENTORY_ISSUE_CHART_COLORS.success,
    },
    {
      id: 'low_stock',
      label: t('inventoryHealth.lowStock'),
      count: summary.lowStock,
      percentage: summary.lowStockPercentage,
      color: 'warning',
      chartColor: INVENTORY_ISSUE_CHART_COLORS.warning,
    },
    {
      id: 'out_of_stock',
      label: t('inventoryHealth.outOfStock'),
      count: summary.outOfStock,
      percentage: summary.outOfStockPercentage,
      color: 'danger',
      chartColor: INVENTORY_ISSUE_CHART_COLORS.danger,
    },
  ]

  return {
    total: summary.totalActiveItems,
    rows,
  }
}

/** Dashboard shortcuts. Callers hide entries the user cannot open. */
export function inventoryQuickActions(t: InventoryHealthTranslate): InventoryQuickAction[] {
  return [
    {
      id: 'adjust_stock',
      label: t('inventoryHealth.quickActionAdjustStock'),
      icon: 'inventory',
      color: 'info',
      to: { name: 'inventory-stock-out' },
      permission: { action: 'create', subject: 'stock-out' },
    },
    {
      id: 'view_reports',
      label: t('inventoryHealth.quickActionViewReports'),
      icon: 'description',
      color: 'purple',
      to: { name: 'items-reports' },
      permission: { action: 'read', subject: 'item' },
    },
    {
      id: 'manage_items',
      label: t('inventoryHealth.quickActionManageItems'),
      icon: 'settings',
      color: 'success',
      to: { name: 'inventory-items' },
      permission: { action: 'read', subject: 'item' },
    },
    {
      id: 'stock_in',
      label: t('inventoryHealth.quickActionStockIn'),
      icon: 'add_box',
      color: 'secondary',
      to: { name: 'inventory-stock-in' },
      permission: { action: 'create', subject: 'stock-in' },
    },
  ]
}

export function inventoryHealthFilterLabel(
  filter: InventoryHealthFilter | null,
  t: InventoryHealthTranslate,
): string | null {
  if (!filter) return null
  const parts: string[] = []
  if (filter.stock_status === 'low_stock') parts.push(t('inventoryHealth.lowStock'))
  if (filter.stock_status === 'out_of_stock') parts.push(t('inventoryHealth.outOfStock'))
  if (filter.stock_status === 'in_stock') parts.push(t('inventoryHealth.inStock'))
  if (filter.expiry_status === 'expiring_soon') parts.push(t('inventoryHealth.expiringSoon'))
  if (filter.expiry_status === 'expired') parts.push(t('inventoryHealth.expired'))
  return parts.length > 0 ? parts.join(', ') : null
}

/** Map a single-dimension health filter to its chip/card id. Mixed filters return null. */
export function inventoryHealthCardIdFromFilter(filter: InventoryHealthFilter | null): InventoryHealthCardId | null {
  if (!filter) return null
  if (filter.stock_status && filter.expiry_status) return null
  if (filter.stock_status === 'low_stock') return 'low_stock'
  if (filter.stock_status === 'out_of_stock') return 'out_of_stock'
  if (filter.expiry_status === 'expiring_soon') return 'expiring_soon'
  if (filter.expiry_status === 'expired') return 'expired'
  return null
}

export function inventoryHealthFilterFromQuery(query: LocationQuery): InventoryHealthFilter | null {
  const stock = Array.isArray(query.stock_status) ? query.stock_status[0] : query.stock_status
  const expiry = Array.isArray(query.expiry_status) ? query.expiry_status[0] : query.expiry_status
  const filter: InventoryHealthFilter = {}

  if (typeof stock === 'string' && STOCK_STATUSES.includes(stock as InventoryStockStatus)) {
    filter.stock_status = stock as InventoryStockStatus
  }
  if (typeof expiry === 'string' && EXPIRY_STATUSES.includes(expiry as InventoryExpiryStatus)) {
    filter.expiry_status = expiry as InventoryExpiryStatus
  }

  return filter.stock_status || filter.expiry_status ? filter : null
}

export function itemsRouteQueryForHealthFilter(filter: InventoryHealthFilter): Record<string, string> {
  const query: Record<string, string> = {}
  if (filter.stock_status) query.stock_status = filter.stock_status
  if (filter.expiry_status) query.expiry_status = filter.expiry_status
  return query
}

/** True when any attention card count is non-zero (aligned with dashboard cards, not batch-only). */
export function hasInventoryHealthIssues(summary: InventoryHealthSummary): boolean {
  return inventoryAttentionCount(summary) > 0
}

/** Remaining qty for the active warehouse from a list row's `expiry_summary`. */
export function itemExpiryQtyForStatus(
  row: { expiry_summary?: { expired_qty?: number; expiring_soon_qty?: number } | null } | null | undefined,
  status: InventoryExpiryStatus,
): number {
  const summary = row?.expiry_summary
  if (!summary) return 0
  const raw = status === 'expired' ? summary.expired_qty : summary.expiring_soon_qty
  const n = Number(raw)
  return Number.isFinite(n) ? n : 0
}
